
import React, { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import RecipeGenerator from "./RecipeGenerator";
import { db } from "@/integrations/cloudflare/client";

interface EmbeddableRecipeGeneratorProps {
  theme?: string;
  id?: string;
  postId?: string;
}

/**
 * Embeddable version of the Recipe Generator that can be used inside blog posts
 */
const EmbeddableRecipeGenerator: React.FC<EmbeddableRecipeGeneratorProps> = ({ 
  theme = "default",
  id = "embedded",
  postId
}) => {
  const [instanceId] = useState(`${id}_${Math.random().toString(36).substring(2, 9)}`);
  const location = useLocation();
  const [extractedPostId, setExtractedPostId] = useState<string | undefined>(postId);
  
  // Try to extract post ID from URL if not provided
  useEffect(() => {
    if (!postId) {
      // Check if we're on a blog post page
      const pathParts = location.pathname.split('/');
      if (pathParts.includes('blog') && pathParts.length > 2) {
        // Attempt to find the post ID based on slug
        const getPostIdFromSlug = async (slug: string) => {
          try {
            const { data: post } = await db
              .from('blog_posts')
              .select('id')
              .eq('slug', slug)
              .single();
              
            if (post?.id) {
              setExtractedPostId(post.id);
            }
          } catch (error) {
            console.log('Could not determine post ID for tracking');
          }
        };
        
        const slug = pathParts[pathParts.length - 1];
        getPostIdFromSlug(slug);
      }
    }
  }, [location, postId]);

  return (
    <div className="recipe-generator-embed my-8">
      <RecipeGenerator theme={theme} instanceId={instanceId} postId={extractedPostId} />
    </div>
  );
};

/**
 * Function to embed the recipe generator in any HTML element
 */
export const embedRecipeGenerator = (elementId: string, theme: string = "default", postId?: string) => {
  import('react-dom/client').then(({ createRoot }) => {
    const container = document.getElementById(elementId);
    if (container) {
      const root = createRoot(container);
      root.render(<EmbeddableRecipeGenerator theme={theme} id={elementId} postId={postId} />);
    }
  });
};

// Expose the embed function to the global window object
// This allows it to be called from other scripts
if (typeof window !== 'undefined') {
  (window as any).embedElfRecipeGenerator = embedRecipeGenerator;
}

export default EmbeddableRecipeGenerator;
